"use client";
import React from "react";
import { usePathname } from "next/navigation";
import { useGetAuthenticatedUser } from "@gettactic/helpers/src/hooks/useGetAuthenticatedUser";
import { api } from "@/lib/api/api";
import { isHostnameOrganizationSubDomain } from "@gettactic/helpers/src/url/isHostnameOrganizationSubDomain";
import { getOrganizationNameFromHostname } from "@gettactic/helpers/src/url/getOrganizationNameFromHostname";
import { getOrganizationUrlFromSlug } from "@gettactic/helpers/src/url/getOrganizationUrlFromSlug";
import { isAppHostname } from "@gettactic/helpers/src/url/isAppHostname";
import { AppAuthenticatedProvider } from "@gettactic/hooks";
import { isStationBotRole } from "@gettactic/api";
import { ApiSettingsProvider } from "@cmp-src/providers/ApiSettingsProvider";
import { replaceLocationHref } from "@gettactic/helpers/src/navigation/replaceLocationHref";
import { TacticProvider } from "@cmp-src/providers/TacticProvider/TacticProvider";
import { EmbeddedLoginScreen } from "@/features/embedded/views/EmbeddedLoginScreen";
import { FeatureAnnouncementProvider } from "@/providers/FeatureAnnouncementProvider";
import { useTeamsApp } from "@/features/microsoft-teams/hooks/useTeamsApp";
import { isVercelDeployment } from "@/providers/auth/SlugSelector";

export const SecondaryLayoutProviders: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const teamsClient = useTeamsApp();

  const pathname = usePathname();
  const isAppSubdomain = isAppHostname();
  const isVercel = isVercelDeployment();
  const isMicrosoftTeamsRoute = pathname?.startsWith("/microsoft-teams/");
  const isMicrosoftTeamsTabAuthDoneRoute =
    pathname?.startsWith("/microsoft-teams/tab-auth-done");
  if (isAppSubdomain || isMicrosoftTeamsRoute) api.setEmbeddedSettings();

  const isEmbedded = isAppSubdomain || isMicrosoftTeamsRoute;

  const { user: authenticatedUser, isLoading } = useGetAuthenticatedUser(
    api,
    isEmbedded,
    (isEmbedded || isVercel) ? { allowRedirect: false } : undefined
  );
  const firstOrganizationSlug =
    authenticatedUser?.organization?.slug ||
    authenticatedUser?.organizations?.[0]?.slug ||
    "";

  const isSubDomain = isHostnameOrganizationSubDomain();

  const isSetupRoute = pathname?.startsWith("/setup");
  React.useEffect(() => {
    if (isVercel) return;
    const organizationName = getOrganizationNameFromHostname();
    if (
      !isMicrosoftTeamsRoute &&
      firstOrganizationSlug &&
      (!isSubDomain || !organizationName)
    ) {
      replaceLocationHref(getOrganizationUrlFromSlug(firstOrganizationSlug));
    }
  }, [isSubDomain, firstOrganizationSlug, isMicrosoftTeamsRoute, isVercel]);
  React.useEffect(() => {
    if ((isAppSubdomain || isMicrosoftTeamsRoute) && teamsClient && authenticatedUser) {
      teamsClient.app.notifySuccess();
    }
  }, [authenticatedUser, isAppSubdomain, isMicrosoftTeamsRoute, teamsClient]);

  // Redirect station bot users to /kiosk — they must not access normal web routes
  const roles = authenticatedUser?.user?.organization_user?.roles;
  const isBotStation = roles?.some(isStationBotRole) ?? false;
  React.useEffect(() => {
    if (isBotStation) {
      replaceLocationHref("/kiosk");
    }
  }, [isBotStation]);

  if (isBotStation) return null;

  // On Vercel deployments, skip the subdomain check — VercelSlugProvider handles slug selection
  if (!isVercel && !isSubDomain && !isMicrosoftTeamsRoute && !isSetupRoute) return null;

  if (isMicrosoftTeamsTabAuthDoneRoute) {
    return <>{children}</>;
  }

  if (
    (isAppSubdomain || isMicrosoftTeamsRoute) &&
    !isLoading &&
    !authenticatedUser &&
    !isSetupRoute &&
    !isMicrosoftTeamsTabAuthDoneRoute
  ) {
    return <EmbeddedLoginScreen />;
  }

  return (
    <>
      {authenticatedUser ? (
        <AppAuthenticatedProvider
          api={api}
          authenticatedUser={authenticatedUser}
        >
          <TacticProvider>
            <ApiSettingsProvider>
              <FeatureAnnouncementProvider>
                {children}
              </FeatureAnnouncementProvider>
            </ApiSettingsProvider>
          </TacticProvider>
        </AppAuthenticatedProvider>
      ) : null}
    </>
  );
};
